/**
 * Pure helpers for `conditionalPathsGroups` array mutations:
 * move up/down, duplicate (strip ids so Voluum mints fresh ones), delete.
 *
 * Used by the left column when wiring `PathGroupCard` action callbacks.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  VoluumPathGroupUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

function clonePath(path: VoluumPathUpdate): VoluumPathUpdate {
  const { id: _drop, ...rest } = path;
  return JSON.parse(JSON.stringify(rest)) as VoluumPathUpdate;
}

export function cloneGroupForDuplicate(
  group: VoluumPathGroupUpdate,
): VoluumPathGroupUpdate {
  const { id: _dropId, ...rest } = group;
  const cloned = JSON.parse(JSON.stringify(rest)) as VoluumPathGroupUpdate;
  cloned.paths = (group.paths ?? []).map(clonePath);
  return cloned;
}

function swap<T>(arr: T[], a: number, b: number): T[] {
  const next = arr.slice();
  const tmp = next[a] as T;
  next[a] = next[b] as T;
  next[b] = tmp;
  return next;
}

export function moveGroupUp(
  groups: VoluumPathGroupUpdate[],
  index: number,
): VoluumPathGroupUpdate[] {
  if (index <= 0 || index >= groups.length) return groups;
  return swap(groups, index, index - 1);
}

export function moveGroupDown(
  groups: VoluumPathGroupUpdate[],
  index: number,
): VoluumPathGroupUpdate[] {
  if (index < 0 || index >= groups.length - 1) return groups;
  return swap(groups, index, index + 1);
}

export function duplicateGroup(
  groups: VoluumPathGroupUpdate[],
  index: number,
): VoluumPathGroupUpdate[] {
  const source = groups[index];
  if (!source) return groups;
  const next = groups.slice();
  next.splice(index + 1, 0, cloneGroupForDuplicate(source));
  return next;
}

export function deleteGroup(
  groups: VoluumPathGroupUpdate[],
  index: number,
): VoluumPathGroupUpdate[] {
  if (index < 0 || index >= groups.length) return groups;
  return groups.filter((_, i) => i !== index);
}
